// Second sign-in step for accounts with MFA (AUTH-02): TOTP code or passkey, via POST /auth/stepup.
import { useRef, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { browserSupportsWebAuthn, startAuthentication } from "@simplewebauthn/browser";
import { normalizeDigits } from "@ady/format";
import { t } from "@ady/i18n";
import { Banner, Button, Field, focusFirstInvalid, Input } from "@ady/ui";
import { api } from "../api/client";
import { errorMessage } from "../api/errors";
import { keys } from "../api/queries";
import { useGo } from "../shell/AppLink";
import { TID } from "../testids";
import { AuthLayout } from "./AuthLayout";
import { nextPath } from "./SignIn";

export function MfaPage() {
  const [code, setCode] = useState("");
  const [fieldError, setFieldError] = useState<string>();
  const [formError, setFormError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const go = useGo();
  const qc = useQueryClient();

  const done = async () => {
    await qc.invalidateQueries({ queryKey: keys.me });
    go(nextPath());
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setFieldError(t("auth.codeInvalid"));
      requestAnimationFrame(() => focusFirstInvalid(formRef.current));
      return;
    }
    setFieldError(undefined);
    setBusy(true);
    try {
      await api.stepUp({ method: "totp", code });
      await done();
    } catch (err) {
      setFieldError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const passkey = async () => {
    setFormError(undefined);
    if (!browserSupportsWebAuthn()) return setFormError(t("auth.passkeyUnsupported"));
    try {
      const opts = await api.passkeyOptions("stepup");
      const response = await startAuthentication({ optionsJSON: opts.options as never });
      await api.stepUp({ method: "passkey", challenge_id: opts.challenge_id, response });
      await done();
    } catch (err) {
      setFormError(
        err instanceof Error && err.name === "NotAllowedError"
          ? t("auth.passkeyCancelled")
          : errorMessage(err),
      );
    }
  };

  return (
    <AuthLayout title={t("auth.mfa.title")} lead={t("auth.mfa.lead")} testId={TID.mfaPage}>
      {formError ? <Banner kind="error" title={formError} data-testid={TID.formError} /> : null}
      <form ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Field label={t("auth.mfa.totpLabel")} error={fieldError} required>
          <Input
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) =>
              setCode(normalizeDigits(e.target.value).replace(/\D/g, "").slice(0, 6))
            }
            ledger
            data-testid={TID.mfaCode}
            autoFocus
          />
        </Field>
        <Button
          icon="shieldCheck"
          type="submit"
          fullWidth
          loading={busy}
          loadingLabel={t("auth.verifying")}
          data-testid={TID.mfaVerify}
        >
          {t("auth.mfa.verify")}
        </Button>
      </form>
      <Button variant="secondary" fullWidth icon="fingerprint" onClick={passkey}>
        {t("auth.mfa.usePasskey")}
      </Button>
    </AuthLayout>
  );
}
